import { collection, query, where, getDocs, doc, addDoc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { Collaborator } from '@/models';
import { CollaboratorFormData } from '@/features/Collaborators/Edit/Types';
import { extractSalaryFields, updateSalaryInSubcollection } from './salarySubcollectionWriteService';

export const processCollaboratorDataForSubmission = (formData: CollaboratorFormData) => {
    const dataToSubmit = { ...formData };

    // Temporarily disabled - salary data moved to separate collection
    // This logic will be restored in Phase 4 with proper sensitive data handling
    /*
    if (dataToSubmit.salaries && dataToSubmit.salaries.length > 0) {
        const sortedSalaries = [...dataToSubmit.salaries].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        const mostRecentSalary = sortedSalaries[0];
        dataToSubmit.salaryAmount = mostRecentSalary.amount;
        dataToSubmit.salaryType = mostRecentSalary.salaryType;
        delete dataToSubmit.salaries;
    }
    */

    const fileFields: (keyof CollaboratorFormData)[] = [
        'idDocumentFrontFile', 'idDocumentBackFile', 'workContractFile', 'ndaFile',
        'equipmentUsageFile', 'imageRightsFile', 'admissionExamFile', 'terminationExamFile',
        'internshipContractFile', 'marriageCertificateFile', 'spouseIdentityFile', 
        'motherIdentityFile', 'fatherIdentityFile', 'pcdDocumentFile'
    ];

    fileFields.forEach(field => {
        delete (dataToSubmit as any)[field];
    });

    if (dataToSubmit.addresses) {
        dataToSubmit.addresses.forEach(addr => delete addr.proofOfAddressFile);
    }
    if (dataToSubmit.medicalCertificates) {
        dataToSubmit.medicalCertificates.forEach(cert => {
            delete cert.certificateFile;
            delete cert.id;
        });
    }
    if (dataToSubmit.children) {
        dataToSubmit.children.forEach(child => {
            delete child.birthCertificateFile;
            delete child.identityDocumentFile;
        });
    }
    if (dataToSubmit.periodicExams) {
        dataToSubmit.periodicExams.forEach(exam => {
            delete exam.examFile;
            delete exam.id;
        });
    }

    const { id, ...collaboratorData } = dataToSubmit;

    if (collaboratorData.vacationPeriods) {
        collaboratorData.vacationPeriods = collaboratorData.vacationPeriods.map(({ id, ...rest }) => rest);
    }
    if (collaboratorData.paidRestPeriods) {
        collaboratorData.paidRestPeriods = collaboratorData.paidRestPeriods.map(({ id, ...rest }) => rest);
    }
    if (collaboratorData.maritalStatus === undefined) {
        (collaboratorData as any).maritalStatus = null;
    }
    if (collaboratorData.sex === undefined) {
        (collaboratorData as any).sex = null;
    }

    const fieldsToNullify: (keyof Omit<CollaboratorFormData, 'id'>)[] = [
        'gender', 'race', 'healthInsuranceProvider', 'shirtSize', 'healthInsuranceInclusionDate', 'healthInsuranceCardNumber', 'slipperSize', 'transportationBenefitAmount',
        'freeTimeActivities', 'favoriteFoodAndDrink', 'favoriteColor', 'favoriteBook', 'favoriteMovieOrSeries', 'favoriteCharacter', 'sportsPractice', 'specialTalent', 'futureGoals', 'generalObservations',
        'workSchedule', 'paidRestObservations'
    ];

    fieldsToNullify.forEach(field => {
        if (collaboratorData[field] === undefined) {
            (collaboratorData as any)[field] = null;
        }
    });

    return {
        id: dataToSubmit.id,
        collaboratorData: dataToSubmit
    };
};

export const getCollaboratorIdByEmail = async (email: string): Promise<string | null> => {
    try {
        const q = query(collection(db, 'collaborators_private'), where("email", "==", email));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            return null;
        } else {
            return querySnapshot.docs[0].id;
        }
    } catch (error) {
        console.error("Error fetching collaborator ID by email: ", error);
        throw error;
    }
};

export const collaboratorService = {
    async getCollaboratorById(id: string): Promise<Collaborator | null> {
        if (!id) return null;
        try {
            const collaboratorRef = doc(db, 'collaborators_private', id);
            const docSnap = await getDoc(collaboratorRef);

            if (docSnap.exists()) {
                return { id: docSnap.id, ...docSnap.data() } as Collaborator;
            } else {
                // Document not found - this is handled by returning null
                return null;
            }
        } catch (error) {
            console.error("Error fetching collaborator by ID: ", error);
            throw error;
        }
    },

    async getAllCollaborators(): Promise<Collaborator[]> {
        const q = query(collection(db, 'collaborators_private'));
        const querySnapshot = await getDocs(q);
        const collaboratorsData = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
        })) as Collaborator[];
        return collaboratorsData;
    },

    async getCollaboratorByEmail(email: string): Promise<Collaborator | null> {
        if (!email) return null;
        try {
            const q = query(collection(db, 'collaborators_private'), where("email", "==", email));
            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                return null;
            } else {
                const doc = querySnapshot.docs[0];
                return { id: doc.id, ...doc.data() } as Collaborator;
            }
        } catch (error) {
            console.error("Error fetching collaborator by email: ", error);
            throw error;
        }
    },

    async addCollaborator(formData: CollaboratorFormData): Promise<string> {
        // Extract salary fields FIRST (before processing)
        const { salaryFields, nonSalaryFields } = extractSalaryFields(formData);

        // Process non-salary data for submission
        const { collaboratorData } = processCollaboratorDataForSubmission(nonSalaryFields);

        // Add main document (non-salary data)
        const docRef = await addDoc(collection(db, 'collaborators_private'), collaboratorData as any);
        const newCollaboratorId = docRef.id;

        // Add salary subcollection if salary data exists
        if (salaryFields) {
            try {
                await updateSalaryInSubcollection(newCollaboratorId, salaryFields);
                console.log('✅ Salary data saved to subcollection for new collaborator');
            } catch (error) {
                console.error('Error saving salary data to subcollection:', error);
                // Don't throw - main data was saved successfully
            }
        }

        return newCollaboratorId;
    },

    async updateCollaborator(formData: CollaboratorFormData): Promise<void> {
        if (!formData.id) {
            throw new Error("ID do colaborador é necessário para a atualização.");
        }

        // DEBUG: Log completo dos dados recebidos
        console.log('🔍 collaboratorService.updateCollaborator - dados recebidos:', {
            hasDeletedIds: !!(formData as any).salaryHistoryDeletedIds?.length,
            deletedIds: (formData as any).salaryHistoryDeletedIds,
            hasSalaries: !!(formData as any).salaryHistorySalaries?.length,
            salariesCount: (formData as any).salaryHistorySalaries?.length
        });

        // Extract salary fields FIRST (before processing)
        const { salaryFields, nonSalaryFields } = extractSalaryFields(formData);

        // DEBUG: Log após extração
        console.log('🔍 Após extractSalaryFields:', {
            hasSalaryFields: !!salaryFields,
            salaryFieldKeys: salaryFields ? Object.keys(salaryFields) : [],
            deletedIds: salaryFields?.salaryHistoryDeletedIds,
            hasDeletedIds: !!(salaryFields?.salaryHistoryDeletedIds?.length)
        });

        // Process non-salary data for submission
        const { id, collaboratorData } = processCollaboratorDataForSubmission(nonSalaryFields);

        // Update main document (non-salary data)
        const collaboratorRef = doc(db, 'collaborators_private', id);
        await updateDoc(collaboratorRef, collaboratorData as any);

        // Update salary subcollection if salary data exists
        if (salaryFields) {
            try {
                await updateSalaryInSubcollection(id, salaryFields);
                console.log('✅ Salary data saved to subcollection');
            } catch (error) {
                console.error('Error saving salary data to subcollection:', error);
                // Don't throw - main data was saved successfully
                // Log error but don't fail the entire update
            }
        }
    },

    async updatePartialCollaborator(
        { id, data }: { id: string; data: Partial<CollaboratorFormData> }
    ): Promise<void> {
        if (!id) {
            throw new Error("ID do colaborador é necessário para a atualização parcial.");
        }

        // Extract salary fields from partial data
        const { salaryFields, nonSalaryFields } = extractSalaryFields(data);

        // Update main document (non-salary fields)
        if (Object.keys(nonSalaryFields).length > 0) {
            const collaboratorRef = doc(db, 'collaborators_private', id);
            await updateDoc(collaboratorRef, nonSalaryFields);
        }

        // Update salary subcollection if salary data exists
        if (salaryFields) {
            try {
                await updateSalaryInSubcollection(id, salaryFields);
                console.log('✅ Partial salary data saved to subcollection');
            } catch (error) {
                console.error('Error saving partial salary data:', error);
                // Don't throw - main data was saved successfully
            }
        }
    },

    async updateCollaboratorRating(id: string, rating: number): Promise<void> {
        if (!id) {
            throw new Error("ID do colaborador é necessário para atualizar o rating.");
        }
        if (rating < 1 || rating > 10) {
            throw new Error("Rating deve estar entre 1 e 10.");
        }
        const collaboratorRef = doc(db, 'collaborators_private', id);
        await updateDoc(collaboratorRef, { rating });
    },

    async updateCollaboratorSalary(id: string, salary: number): Promise<void> {
        if (!id) {
            throw new Error("ID do colaborador é necessário para atualizar o salário.");
        }
        if (salary < 0) {
            throw new Error("Salário deve ser um valor positivo.");
        }
        const collaboratorRef = doc(db, 'collaborators_private', id);
        await updateDoc(collaboratorRef, { salary });
    },

    async updateCollaboratorResources(id: string, resources: any[]): Promise<void> {
        if (!id) {
            throw new Error("ID do colaborador é necessário para atualizar os recursos.");
        }
        const collaboratorRef = doc(db, 'collaborators_private', id);
        await updateDoc(collaboratorRef, { resources });
    },

    async updateCollaboratorStatus(id: string, statusUpdate: Record<string, any>): Promise<void> {
        if (!id) {
            throw new Error("ID do colaborador é necessário para atualizar o status.");
        }
        const collaboratorRef = doc(db, 'collaborators_private', id);
        await updateDoc(collaboratorRef, statusUpdate);
    },
};
